import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  IconButton,
  Tooltip,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Snackbar,
  Tabs,
  Tab,
  useMediaQuery,
  TextField,
  Alert,
  Pagination,
  CircularProgress,
  Menu,
  MenuItem
} from '@mui/material';
import {
  AutoFixHigh as AIIcon,
  VideoCall as VideoIcon,
  ZoomIn as ZoomIcon,
  ContentCopy as CopyIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  MoreVert as MoreIcon,
  Description as ContentIcon
} from '@mui/icons-material';
import AccountLayout from '../../layouts/AccountLayout';
import ImageGenerationDialog from '../../components/MicroSite/Page/ImageGenerationDialog';
import VideoGenerationDialog from '../../components/MicroSite/Page/VideoGenerationDialog';
import { useParams } from 'react-router-dom';
import { getLocalImageUrl, getLocalVideoUrl } from '../../utils/imageUtils';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

// TabPanel component to handle tab content
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`ai-media-tabpanel-${index}`}
      aria-labelledby={`ai-media-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function AIImageGeneration() {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const [isGenerateImageOpen, setIsGenerateImageOpen] = useState(false);
  const [isGenerateVideoOpen, setIsGenerateVideoOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [images, setImages] = useState([]);
  const [stats, setStats] = useState({
    totalImages: 0,
    totalCost: 0,
    storageUsed: '0 MB',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [previewDialog, setPreviewDialog] = useState({
    open: false,
    image: null,
    video: null,
    type: null // 'image' or 'video'
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: ''
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Tab state
  const [tabValue, setTabValue] = useState(0);
  
  // Videos state
  const [videos, setVideos] = useState([]);
  const [videosLoading, setVideosLoading] = useState(false);
  const [videoError, setVideoError] = useState(null);
  const [videoRefreshTrigger, setVideoRefreshTrigger] = useState(0);
  
  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Dialog state
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [copyUrl, setCopyUrl] = useState('');

  const handleTabChange = (event, newValue) => {
    if (newValue === 2) {
      // Navigate to AI Content page
      navigate(`/accounts/${accountId}/ai-content`);
      return;
    }
    setTabValue(newValue);
    
    // Only fetch videos when switching to the videos tab
    if (newValue === 1 && videos.length === 0) {
      fetchVideos();
    }
  };

  const fetchImages = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `/api/images/generated/${accountId}?page=${page}&limit=12`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      
      if (!response.ok) throw new Error('Failed to fetch images');
      
      const data = await response.json();
      
      // Transform the image URLs to use our local endpoint
      const transformedImages = data.images.map(img => ({
        ...img,
        localUrl: getLocalImageUrl(img.url, img.id)
      }));
      
      setImages(transformedImages);
      setStats(data.statistics);
      setTotalPages(data.pagination.pages);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchVideos = useCallback(async () => {
    if (!accountId) return;
    
    setVideosLoading(true);
    setVideoError(null);
    
    try {
      console.log('Fetching videos for account:', accountId);
      const response = await fetch(`/api/ai/videos?accountId=${accountId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch videos');
      }
      
      const data = await response.json();
      console.log('Fetched videos:', data.videos);
      setVideos(data.videos || []);
    } catch (error) {
      console.error('Error fetching videos:', error);
      setVideoError(error.message);
    } finally {
      setVideosLoading(false);
    }
  }, [accountId, videoRefreshTrigger]);

  useEffect(() => {
    fetchImages();
    // Only fetch videos initially if we're on the videos tab
    if (tabValue === 1) {
      fetchVideos();
    }
  }, [page, accountId]); // Don't include fetchVideos in the dependency array

  const handleImageGenerated = async (imageData) => {
    try {
      // Store the generated image
      await fetch('/api/images/store', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          ...imageData,
          userId: localStorage.getItem('userId'),
          accountId,
        }),
      });

      // Refresh the images list
      fetchImages();
    } catch (error) {
      console.error('Error storing image:', error);
    }
  };

  const handleVideoGenerated = async (videoData) => {
    console.log('Video generated:', videoData);
    try {
      // Refresh the video list
      setVideoRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error refreshing videos:', error);
    }
  };

  const handlePreviewImage = (image) => {
    setPreviewDialog({
      open: true,
      image,
      video: null,
      type: 'image'
    });
  };

  const handlePreviewVideo = (video) => {
    setPreviewDialog({
      open: true,
      image: null,
      video,
      type: 'video'
    });
  };

  const handleClosePreview = () => {
    setPreviewDialog({
      open: false,
      image: null,
      video: null,
      type: null
    });
  };

  const handleCopyUrl = (url) => {
    navigator.clipboard.writeText(url);
    setSnackbar({
      open: true,
      message: 'URL copied to clipboard!'
    });
  };

  const handleMenuOpen = (event, item) => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedItem(null);
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(copyUrl);
    setCopyDialogOpen(false);
    setSnackbar({
      open: true,
      message: 'URL copied to clipboard!'
    });
  };

  const handleDeleteItem = async () => {
    if (!selectedItem) return;
    
    try {
      const endpoint = tabValue === 0 
        ? `/api/images/generated/${selectedItem.id}` 
        : `/api/videos/generated/${selectedItem.id}`;
        
      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete ${tabValue === 0 ? 'image' : 'video'}`);
      }

      // Refresh the list
      if (tabValue === 0) {
        fetchImages();
      } else {
        setVideoRefreshTrigger(prev => prev + 1);
      }
      
      setSnackbar({
        open: true,
        message: `${tabValue === 0 ? 'Image' : 'Video'} deleted successfully!`
      });
    } catch (error) {
      console.error('Delete error:', error);
      if (tabValue === 0) {
        setError(error.message);
      } else {
        setVideoError(error.message);
      }
    }
    
    handleMenuClose();
  };

  const handleDownload = () => {
    if (!selectedItem) return;
    
    const a = document.createElement('a');
    a.href = selectedItem.localUrl;
    a.download = `ai-${tabValue === 0 ? 'image' : 'video'}-${selectedItem.id}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    
    handleMenuClose();
  };

  const cols = isMobile ? 1 : 3;

  return (
    <AccountLayout>
      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="AI media tabs"
            centered
          >
            <Tab label="AI Images" id="ai-media-tab-0" aria-controls="ai-media-tabpanel-0" />
            <Tab label="AI Videos" id="ai-media-tab-1" aria-controls="ai-media-tabpanel-1" />
            <Tab label="AI Content" id="ai-media-tab-2" aria-controls="ai-media-tabpanel-2" />
          </Tabs>
        </Box>
        
        {/* Images Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">
              AI Generated Images
            </Typography>
            <Button 
              variant="contained" 
              startIcon={<AIIcon />}
              onClick={() => setIsGenerateImageOpen(true)}
            >
              Generate New Image
            </Button>
          </Box>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : images.length === 0 ? (
            <Alert severity="info">No images generated yet.</Alert>
          ) : (
            <>
              <ImageList cols={cols} gap={16}>
                {images.map((image) => (
                  <ImageListItem key={image.id}>
                    <img
                      src={image.localUrl}
                      alt={image.prompt}
                      loading="lazy"
                      style={{ objectFit: 'contain', cursor: 'pointer' }}
                      onClick={() => handlePreviewImage(image)}
                    />
                    <ImageListItemBar
                      title={image.prompt.substring(0, 50) + (image.prompt.length > 50 ? '...' : '')}
                      subtitle={`Created: ${new Date(image.createdAt).toLocaleDateString()}`}
                      actionIcon={
                        <IconButton
                          sx={{ color: 'white' }}
                          aria-label="more options"
                          onClick={(e) => handleMenuOpen(e, image)}
                        >
                          <MoreIcon />
                        </IconButton>
                      }
                    />
                  </ImageListItem>
                ))}
              </ImageList>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination 
                  count={totalPages} 
                  page={page} 
                  onChange={(event, value) => setPage(value)} 
                  color="primary" 
                />
              </Box>
            </>
          )}
        </TabPanel>
        
        {/* Videos Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">
              AI Generated Videos
            </Typography>
            <Button 
              variant="contained" 
              startIcon={<VideoIcon />}
              onClick={() => setIsGenerateVideoOpen(true)}
            >
              Generate New Video
            </Button>
          </Box>
          
          {videoError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {videoError}
            </Alert>
          )}
          
          {videosLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : videos.length === 0 ? (
            <Alert severity="info">No videos generated yet.</Alert>
          ) : (
            <>
              <Grid container spacing={3}>
                {videos.map((video) => (
                  <Grid item xs={12} sm={6} md={4} key={video.id}>
                    <Card>
                      <CardMedia
                        component="video"
                        controls
                        src={video.url}
                        sx={{ height: 200, cursor: 'pointer' }}
                        onClick={() => handlePreviewVideo(video)}
                      />
                      <CardContent>
                        <Typography variant="body2" color="text.secondary">
                          {video.prompt.substring(0, 100) + (video.prompt.length > 100 ? '...' : '')}
                        </Typography>
                        <Typography variant="caption" display="block">
                          Created: {new Date(video.createdAt).toLocaleDateString()}
                        </Typography>
                      </CardContent>
                      <CardActions>
                        <IconButton 
                          aria-label="more options"
                          onClick={(e) => handleMenuOpen(e, video)}
                        >
                          <MoreIcon />
                        </IconButton>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination 
                  count={totalPages} 
                  page={page} 
                  onChange={(event, value) => setPage(value)} 
                  color="primary" 
                />
              </Box>
            </>
          )}
        </TabPanel>
        
        {/* Content Tab - This is just a placeholder since we navigate away */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        </TabPanel>
      </Box>
      
      {/* Menu for options */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          setCopyDialogOpen(true);
          setCopyUrl(selectedItem?.url || selectedItem?.localUrl || '');
          handleMenuClose();
        }}>
          <CopyIcon fontSize="small" sx={{ mr: 1 }} />
          Copy URL
        </MenuItem>
        <MenuItem onClick={handleDownload}>
          <DownloadIcon fontSize="small" sx={{ mr: 1 }} />
          Download
        </MenuItem>
        <MenuItem onClick={handleDeleteItem} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
      
      {/* Dialog for copying URL */}
      <Dialog open={copyDialogOpen} onClose={() => setCopyDialogOpen(false)}>
        <DialogTitle>Copy URL</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            value={copyUrl}
            onChange={(e) => setCopyUrl(e.target.value)}
            margin="dense"
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCopyToClipboard} color="primary">
            Copy to Clipboard
          </Button>
        </DialogActions>
      </Dialog>

      {/* Media preview dialog */}
      <Dialog
        open={previewDialog.open}
        onClose={handleClosePreview}
        maxWidth="lg"
      >
        <DialogTitle>
          {(previewDialog.image || previewDialog.video) && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                {previewDialog.type === 'image' ? 'Image Preview' : 'Video Preview'}
              </Typography>
              <IconButton onClick={handleClosePreview} aria-label="close">
                <CloseIcon />
              </IconButton>
            </Box>
          )}
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {previewDialog.image && previewDialog.type === 'image' && (
            <>
              <img
                src={previewDialog.image.localUrl}
                alt={previewDialog.image.prompt}
                style={{ width: '100%', maxHeight: '60vh', objectFit: 'contain' }}
              />
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">Prompt:</Typography>
                <Typography variant="body1">{previewDialog.image.prompt}</Typography>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                  Created: {new Date(previewDialog.image.createdAt).toLocaleString()}
                </Typography>
              </Box>
            </>
          )}
          {previewDialog.video && previewDialog.type === 'video' && (
            <>
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                <video
                  controls
                  src={previewDialog.video.url}
                  style={{ width: '100%', maxHeight: '60vh' }}
                />
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">Prompt:</Typography>
                <Typography variant="body1">{previewDialog.video.prompt}</Typography>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                  Created: {new Date(previewDialog.video.createdAt).toLocaleString()}
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {(previewDialog.image || previewDialog.video) && (
            <>
              <Button 
                startIcon={<CopyIcon />}
                onClick={() => {
                  const item = previewDialog.type === 'image' ? previewDialog.image : previewDialog.video;
                  handleCopyUrl(item.url || item.localUrl);
                }}
              >
                Copy URL
              </Button>
              <Button 
                startIcon={<DownloadIcon />}
                onClick={() => {
                  setSelectedItem(previewDialog.type === 'image' ? previewDialog.image : previewDialog.video);
                  handleDownload();
                }}
              >
                Download
              </Button>
            </>
          )}
          <Button onClick={handleClosePreview}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />

      {/* Generation Dialogs */}
      <ImageGenerationDialog
        open={isGenerateImageOpen}
        onClose={() => setIsGenerateImageOpen(false)}
        onImageGenerated={handleImageGenerated}
        accountId={accountId}
      />
      
      <VideoGenerationDialog
        open={isGenerateVideoOpen}
        onClose={() => setIsGenerateVideoOpen(false)}
        onVideoGenerated={handleVideoGenerated}
        accountId={accountId}
      />
    </AccountLayout>
  );
}

export default AIImageGeneration; 